import React, { ReactNode, useCallback, useMemo } from 'react'

import { MetricData, YMCallbacks } from './types'
import { createFastContext } from '../utils'
import { prepareData } from './prepare-data'
import { useSendParams } from './use-send-params'

declare global {
	interface Window {
		ym?: any
	}
}

const defaultCbs: YMCallbacks = {
	cart: null,
	cartPay: null,
	cartStep1: null,
	cartStep2: null,
	cartStep3: null,
	searchRoom: null,
	searchTour: null,
	sendLeadForm: null,
	sendReviews: null,
	sendPoll: null,
	clickButton: null,
	clickCard: null,
}

interface YMCtx extends YMCallbacks {}

const { useCtx, Provider } = createFastContext<YMCtx>(defaultCbs)

const YMProvider = ({ children }: { children?: ReactNode }) => {
	const metricId = useMemo(() => (window.ym?.a?.[0]?.[0] as number) || null, [])

	const reachGoal = useCallback(
		({ targetName, data }: { targetName: string; data?: MetricData }) => {
			if (!metricId) return

			window.ym?.(metricId, 'reachGoal', targetName, data)
		},
		[metricId]
	)

	const { addParamsToSet } = useSendParams({ metricId })

	const cbs = useMemo(() => {
		const result = Object.entries(targetNames).reduce(
			(result: YMCallbacks, [key, name]) => {
				return {
					...result,
					[key]: (arg: { data?: MetricData }) => {
						reachGoal({ targetName: name, data: prepareData(arg?.data) })
					},
				}
			},
			defaultCbs
		)

		result.sendLeadForm = arg => {
			const data = prepareData(arg?.data)
			if (!data) return
			reachGoal({
				targetName: targetNames.sendLeadForm,
				data,
			})
		}
		result.sendPoll = arg => {
			const data = prepareData(arg?.data)
			if (!data) return
			reachGoal({
				targetName: targetNames.sendPoll,
				data,
			})
		}

		result.clickButton = arg => {
			if (!arg?.data) return
			addParamsToSet({ type: 'button', data: prepareData(arg.data) })
		}
		result.clickCard = arg => {
			if (!arg?.data) return
			addParamsToSet({ type: 'card', data: prepareData(arg.data) })
		}

		return result
	}, [reachGoal, addParamsToSet])

	return <Provider value={cbs}>{children}</Provider>
}

const targetNames = {
	cart: 'cart',
	cartPay: 'cart.pay',
	cartStep1: 'cart.step1',
	cartStep2: 'cart.step2',
	cartStep3: 'cart.step3',
	searchRoom: 'searchRoom',
	searchTour: 'searchTour',
	sendLeadForm: 'sendLeadForm',
	sendReviews: 'sendReviews',
	sendPoll: 'sendPollForm',
} as const

export { YMProvider, useCtx as useYM }
